(() => {
  'use strict';

  window.clarity =
    window.clarity ||
    function () {
      (window.clarity.q = window.clarity.q || []).push(arguments);
    };

  const clarity = (...args) => window.clarity(...args);
  const body = document.body;

  if (!body) return;

  const sanitize = (value) =>
    String(value || '')
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9._-]+/g, '_')
      .slice(0, 120);

  const setTag = (key, value) => {
    const safeValue = sanitize(value);
    if (safeValue) clarity('set', key, safeValue);
  };

  const sendEvent = (name) => {
    const safeName = sanitize(name);
    if (safeName) clarity('event', safeName);
  };

  const pageType = sanitize(body.dataset.clarityPageType || 'other');
  const template = sanitize(body.dataset.clarityTemplate || pageType);

  setTag('page_type', pageType);
  setTag('template', template);
  setTag('device_view', window.matchMedia('(max-width: 749px)').matches ? 'mobile' : 'desktop');

  if (body.dataset.clarityProduct) {
    setTag('product_handle', body.dataset.clarityProduct);
  }

  const campaignParams = new URLSearchParams(window.location.search);
  setTag(
    'traffic_context',
    window.Shopify?.designMode || campaignParams.has('preview_theme_id')
      ? 'theme_preview'
      : campaignParams.get('re_test') === '1'
        ? 'manual_test'
        : 'live'
  );
  ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term'].forEach((key) => {
    setTag(key, campaignParams.get(key));
  });

  const pageEvents = {
    index: 'view_home',
    product: 'view_product',
    collection: 'view_catalog',
    cart: 'view_cart',
    search: 'view_search',
    page: 'view_page',
  };
  sendEvent(pageEvents[pageType] || 'view_other');

  const maskFormFields = (root = document) => {
    if (root instanceof Element && root.matches('input:not([type="hidden"]), textarea, select')) {
      root.setAttribute('data-clarity-mask', 'true');
    }
    root.querySelectorAll('input:not([type="hidden"]), textarea, select').forEach((field) => {
      field.setAttribute('data-clarity-mask', 'true');
    });
  };

  maskFormFields();

  document.addEventListener(
    'submit',
    (event) => {
      const form = event.target;
      if (!(form instanceof HTMLFormElement)) return;

      const action = form.getAttribute('action') || '';

      if (action.includes('/cart/add')) sendEvent('add_to_cart_intent');
      if (action.includes('/contact')) sendEvent('contact_form_submit');
      if (form.matches('[role="search"], search-form form') || action.includes('/search')) {
        sendEvent('search_submit');
      }
    },
    true
  );

  // Horizon emits the cart update before its Ajax request settles. Only count
  // the completed add after the event promise resolves without an error.
  document.addEventListener('shopify:cart:lines-update', (event) => {
    if (event.action !== 'add' || !event.promise) return;
    event.promise
      .then(({ detail }) => {
        if (detail?.didError) {
          sendEvent('add_to_cart_error');
        } else {
          sendEvent('add_to_cart_success');
        }
      })
      .catch(() => sendEvent('add_to_cart_error'));
  });

  document.addEventListener(
    'click',
    (event) => {
      const target = event.target.closest('a, button, summary, [role="button"]');
      if (!target) return;

      const href = target instanceof HTMLAnchorElement ? target.getAttribute('href') || '' : '';
      const text = sanitize(target.textContent);
      const name = sanitize(target.getAttribute('name'));
      const aria = sanitize(target.getAttribute('aria-label'));

      if (target.matches('#rsi_buy_now_button')) sendEvent('cod_form_open_intent');
      if (target.matches('.rsi-submit_button')) sendEvent('cod_order_submit_intent');
      if (target.matches('.rsi-additionals_checkout_button')) sendEvent('paypal_from_cod_intent');

      if (href.includes('/products/')) sendEvent('product_link_click');
      if (href.includes('/collections/')) sendEvent('catalog_link_click');
      if (href.includes('/pages/seguir-pedido') || /seguir.*pedido|rastrear|tracking/.test(`${text} ${aria}`)) {
        sendEvent('tracking_opened');
      }
      if (href.includes('/pages/contact') || href.startsWith('mailto:')) sendEvent('contact_opened');
      if (href.includes('/checkout') || name === 'checkout' || /finalizar.*pedido|pagar.*agora/.test(text)) {
        sendEvent('checkout_intent');
      }
      if (/comprar.*agora|pedir.*agora|buy.*now/.test(`${text} ${aria}`)) sendEvent('buy_now_click');
      if (/buscar|search/.test(`${aria} ${text}`)) sendEvent('search_opened');
      if (target.tagName === 'SUMMARY') sendEvent('faq_opened');
      if (target.closest('[class*="product-media"], [class*="media-gallery"], [class*="thumbnail"]')) {
        sendEvent('product_media_interaction');
      }
    },
    true
  );

  document.addEventListener(
    'change',
    (event) => {
      const field = event.target;
      if (!(field instanceof HTMLInputElement || field instanceof HTMLSelectElement)) return;

      const name = sanitize(field.name);
      if (name === 'quantity' || name.includes('quantity')) sendEvent('quantity_changed');
      if (name === 'id' || name.includes('option')) sendEvent('variant_changed');
    },
    true
  );

  const reached = new Set();
  const thresholds = [25, 50, 75, 90];

  const trackScrollDepth = () => {
    const scrollable = document.documentElement.scrollHeight - window.innerHeight;
    if (scrollable <= 0) return;

    const percent = Math.round((window.scrollY / scrollable) * 100);
    thresholds.forEach((threshold) => {
      if (percent >= threshold && !reached.has(threshold)) {
        reached.add(threshold);
        sendEvent(`scroll_${threshold}`);
      }
    });
  };

  let scrollTicking = false;
  window.addEventListener(
    'scroll',
    () => {
      if (scrollTicking) return;
      scrollTicking = true;
      window.requestAnimationFrame(() => {
        trackScrollDepth();
        scrollTicking = false;
      });
    },
    { passive: true }
  );

  let codFormViewed = false;
  const recordCodFormView = (root) => {
    if (codFormViewed || !(root instanceof Element)) return;
    const form = root.matches('form._rsi_main_form_element')
      ? root
      : root.querySelector('form._rsi_main_form_element');
    if (form?.dataset.open === 'true') {
      codFormViewed = true;
      sendEvent('cod_form_view');
    }
  };

  const watchDialog = (selector, eventName) => {
    const dialog = document.querySelector(selector);
    if (!dialog) return;
    let wasOpen = dialog.hasAttribute('open');
    new MutationObserver(() => {
      const isOpen = dialog.hasAttribute('open');
      if (isOpen && !wasOpen) sendEvent(eventName);
      wasOpen = isOpen;
    }).observe(dialog, { attributes: true, attributeFilter: ['open'] });
  };

  watchDialog('#cart-drawer', 'cart_drawer_view');
  watchDialog('re-recovery-offer dialog', 'recovery_offer_view');

  new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      mutation.addedNodes.forEach((node) => {
        if (node instanceof Element) {
          maskFormFields(node);
          recordCodFormView(node);
        }
      });
    });
  }).observe(document.body, { childList: true, subtree: true });
})();
